'use client';

import SearchAnalytics from '../Analytics';
import { AnalyticsObserver } from '../AnalyticsObserver';
import { useCriteoAnalytics } from '../AnalyticsObserver/utils';
import { parseBreadCrumbs } from '../BreadCrumb/utils';

import React, { Children, useMemo } from 'react';

import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { PageTypes, SEARCH_RESULT_CONTAINER_ID } from '@/constants/index';
import { getKeyValue } from '@/services/content/data';
import { SearchResultGRS } from '@/services/search/services/GRS.types';
import {
    getSearchResultsType,
    isSearchResultGRS,
} from '@/services/search/services/Grs';
import { isSearchResultLW } from '@/services/search/services/LucidWorks';
import { isBDSite } from '@/services/search/utils';
import {
    COMPONENT_TYPE_UI,
    DISPLAY_RANK,
    KEY_SEARCH_INDEX,
    SEARCH,
    TRACK_TYPE_MULTI_ATTR,
    TRACK_TYPE_NAVIGATION,
    TRAVEL_DESTINATION,
} from '@/src/components/Analytics/constants';
import {
    A11Y_CRITEO_PRODUCT_TILE_SEPARATOR,
    A11Y_CRITEO_TITLE,
    PRODUCT_DISPLAY_STYLE,
} from '@/src/containers/main/constants';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import { SearchResultLW } from '@/types/searchQuery';
import { Box } from '@mui/material';
import { Grid } from '@nxt-costco-com/forge-components';
import { ColorGray80 } from '@nxt-costco-com/forge-design-tokens';
import {
    Analytics,
    CriteoButterflyDisplay,
    CriteoProductTile,
    ProductTileSkeleton,
    ProductTileUI,
    findCriteoPlacement,
    useCheckScreen,
    useCriteo,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

import { LoadingResultsGrid } from './LoadingResults.view';
import {
    getAvailableColumnCount,
    hasAdsToPlace,
    injectAdsIntoSearchResults,
    parseSponsoredProductPlacementPosition,
} from './lib';
import type {
    ExtendedSearchResult,
    ExtendedSearchResultGRS,
    ExtendedSearchResultLW,
    SearchResultsGridProps,
} from './types';

export function SearchResultsGrid({
    pageConfig,
    site,
    lang,
    productTileConfigData,
    composerData,
    searchConfigData,
    fullRowAdEntries,
    isCategoryPage,
    pageType,
    categoryTitle,
    doFocusFirstItem,
    showMultiVariantsAvailability,
    resultsPerPage,
    clpAdPlacementFallbackSettings,
}: SearchResultsGridProps) {
    const product_display_style =
        productTileConfigData?.translationsConfig?.product_display_style ??
        PRODUCT_DISPLAY_STYLE;

    const {
        product_card_display_style,
        show_reviews,
        show_action_button,
        inventory_aware_listings,
        product_tile_data_source,
    } = product_display_style;

    const { isInitialized: isQueryParamsLoaded, queryParams: searchParams } =
        useQueryParams();
    const { searchResult, isSearchResultsLoading, searchService, sortType } =
        useSearchContext();
    const { searchPageData, isLoadingCriteoData } = useCriteo();
    const { isMobile, isTablet } = useCheckScreen();

    const baseKey = isCategoryPage
        ? 'viewCategory'
        : isBDSite(process.env.NEXT_PUBLIC_SITE)
          ? 'viewSearchResult'
          : 'viewSearchResults';

    const inGridPlacementKey = `${baseKey}_API_${isMobile ? 'mobile' : 'desktop'}-ingrid`;
    const inGridPlacement = useMemo(() => {
        return findCriteoPlacement(
            inGridPlacementKey,
            searchPageData?.placements,
        );
    }, [searchPageData, inGridPlacementKey]);

    const butterflyPlacementKey = `${baseKey}_API_${isMobile ? 'mobile' : 'desktop'}-Butterfly`;

    const butterflyPlacements = useMemo(() => {
        const placements = [];

        const firstPlacement = findCriteoPlacement(
            `${butterflyPlacementKey}1`,
            searchPageData?.placements,
        );

        if (firstPlacement) {
            placements.push(firstPlacement);
        }

        const secondPlacement = findCriteoPlacement(
            `${butterflyPlacementKey}2`,
            searchPageData?.placements,
        );

        if (secondPlacement) {
            placements.push(secondPlacement);
        }

        return placements;
    }, [searchPageData, butterflyPlacementKey]);

    const analytics = useCriteoAnalytics({
        placement: inGridPlacement,
    });

    const pageCrumbs = useMemo(() => {
        if (pageType === PageTypes.SEARCH) {
            return parseBreadCrumbs(pageConfig.key_value)
                .filter((_b, index) => index > 0)
                .map(b => b?.name);
        }
        return pageType === PageTypes.CATEGORY
            ? Array.isArray(searchResult?.breadcrumb)
                ? searchResult.breadcrumb
                      .map((b: unknown) => (b as { name?: string })?.name)
                      .filter((name): name is string => name !== undefined)
                : []
            : [];
    }, [pageConfig.key_value, pageType, searchResult?.breadcrumb]);

    const columnCount = getAvailableColumnCount(isMobile, isTablet);
    const sponsoredProductPlacementPosition = useMemo(() => {
        // enable module settings fallback
        if (
            pageType === PageTypes.CATEGORY &&
            (!searchConfigData?.sponsored_product_placement ||
                !searchConfigData?.sponsored_product_placement.length) &&
            clpAdPlacementFallbackSettings
        ) {
            return parseSponsoredProductPlacementPosition(
                clpAdPlacementFallbackSettings,
                isMobile,
                isTablet,
            );
        }

        return parseSponsoredProductPlacementPosition(
            searchConfigData?.sponsored_product_placement,
            isMobile,
            isTablet,
        );
    }, [
        searchConfigData?.sponsored_product_placement,
        isMobile,
        isTablet,
        clpAdPlacementFallbackSettings,
        pageType,
    ]);

    const singleItemGridSize = 12 / columnCount; // This determines the size of the grid items

    const resultsToRender = useMemo(() => {
        if (
            isSearchResultsLoading ||
            !searchResult ||
            isLoadingCriteoData ||
            !isQueryParamsLoaded
        ) {
            return null;
        }

        const searchResultsData =
            searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
            isSearchResultGRS(searchResult)
                ? searchResult.searchResult.results
                : searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS &&
                    isSearchResultLW(searchResult)
                  ? searchResult.docs
                  : [];

        // Get the first butterfly position found
        const butterflyPositionConfig =
            searchConfigData?.external_ingrid_position?.find(
                ({ placement_type }) => {
                    return placement_type === 'criteo_butterFly';
                },
            );

        const butterflyPlacementRowNth =
            butterflyPositionConfig?.placement_row_nth_;

        // If we don't have the config, or didn't get placements back from Criteo, return results as-is
        if (
            !hasAdsToPlace(
                Boolean(searchConfigData?.enable_criteo_sponsored_products),
                Boolean(inGridPlacement?.products?.length),
                butterflyPlacementRowNth?.[0],
                butterflyPlacements.length > 0,
            )
        ) {
            return searchResultsData as ExtendedSearchResult[];
        }

        // Merge sponsored products and butterfly placements into one ad insert map so we can handle them in one pass when rendering the grid
        const isSponsoredProductsEnabled = Boolean(
            searchConfigData?.enable_criteo_sponsored_products,
        );
        const inGridSponsoredProducts = isSponsoredProductsEnabled
            ? inGridPlacement?.products
            : undefined;

        return injectAdsIntoSearchResults(
            searchResultsData,
            inGridSponsoredProducts,
            isSponsoredProductsEnabled
                ? sponsoredProductPlacementPosition
                : undefined,
            butterflyPlacements,
            butterflyPositionConfig,
            columnCount,
        );
    }, [
        butterflyPlacements,
        columnCount,
        inGridPlacement?.products,
        isLoadingCriteoData,
        isQueryParamsLoaded,
        isSearchResultsLoading,
        searchConfigData?.enable_criteo_sponsored_products,
        searchConfigData?.external_ingrid_position,
        searchResult,
        searchService,
        sponsoredProductPlacementPosition,
    ]);

    // Loading state skeletons
    if (isSearchResultsLoading || isLoadingCriteoData || !isQueryParamsLoaded) {
        return <LoadingResultsGrid singleItemGridSize={singleItemGridSize} />;
    }

    // Not in loading state, but have no results
    if (!searchResult || !resultsToRender) {
        return null;
    }

    const rowToRenderFullRowAdOn = searchConfigData?.ingrid_placement_row_nth_;
    const hasInGridFullRowAds = Boolean(composerData?.inGridPlacements?.length);

    const a11yCriteo = {
        a11yTitle: getKeyValue(A11Y_CRITEO_TITLE, pageConfig),
        a11yProductTileSeperator: getKeyValue(
            A11Y_CRITEO_PRODUCT_TILE_SEPARATOR,
            pageConfig,
        ),
    };

    const fullRowAdEntriesArray = Children.toArray(fullRowAdEntries);
    let attributionToken = '';
    let correctedSearchTerm = '';
    let xFusionExitCode = '';
    let fusionQueryId = '';
    let searchType = null;
    let grsFilterEvent = {};
    let pageCategories: string[] = [];
    if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
        attributionToken = (searchResult as SearchResultGRS)?.searchResult
            ?.attributionToken;
        correctedSearchTerm = (searchResult as SearchResultGRS)?.searchResult
            ?.correctedQuery;
        searchType = getSearchResultsType(searchResult as SearchResultGRS);
        grsFilterEvent = (searchResult as SearchResultGRS)?.filterEvent || {};
        pageCategories =
            (searchResult as SearchResultGRS)?.pageCategories || [];
    } else {
        correctedSearchTerm =
            (searchResult as SearchResultLW)?.metrics?.correction || '';
        fusionQueryId = (searchResult as SearchResultLW)?.metrics?.queryId;
        xFusionExitCode =
            (searchResult as SearchResultLW)?.metrics?.xFusionExitCode || '';
    }

    return (
        <Grid
            container
            style={{ margin: 0 }}
            spacing={isMobile ? 4 : 6}
            id={SEARCH_RESULT_CONTAINER_ID}
            sx={{
                '& > div': {
                    borderBottom: `1px solid ${ColorGray80}`,
                    marginBottom: '-1px',
                    paddingTop: '24px',
                    paddingBottom: '24px',
                },
                '& > div:empty': {
                    display: 'none',
                },
                borderBottom: `1px solid ${ColorGray80}`,
            }}
        >
            <SearchAnalytics
                searchResults={resultsToRender}
                searchService={searchService}
                pageType={pageType}
                categoryTitle={categoryTitle}
                site={site}
                lang={lang}
                resultsPerPage={resultsPerPage}
                pageCrumbs={pageCrumbs}
                attributionToken={attributionToken}
                correctedSearchTerm={correctedSearchTerm}
                sortType={sortType}
                xFusionExitCode={xFusionExitCode}
                fusionQueryId={fusionQueryId}
                searchType={searchType}
                grsFilterEvent={grsFilterEvent}
                pageCategories={pageCategories}
                searchParams={searchParams}
            />

            {resultsToRender.map((doc, index) => {
                let ProductTileComponent;
                let criteoInGridOnLoadBeaconFired = false;

                if (doc.isCriteoProductTileAd) {
                    if (inGridPlacement?.products?.length) {
                        const product =
                            typeof doc.criteoIndex === 'number' &&
                            inGridPlacement.products[doc.criteoIndex];

                        if (product) {
                            ProductTileComponent = (
                                <Analytics
                                    analyticData={{
                                        adItem: {
                                            title: 'sponsored products',
                                            type: 'product',
                                            style: 'block',
                                        },
                                        itemCuration: 'criteo',
                                        position: 'in grid',
                                        title: 'sponsored products - search in grid',
                                        trackType: 'ad',
                                    }}
                                >
                                    <CriteoProductTile
                                        key={`${product.ParentSKU}_criteo_${doc.criteoIndex}_tile`}
                                        variant={product_card_display_style}
                                        locale={lang}
                                        showActionButton={show_action_button}
                                        showReviews={show_reviews}
                                        showCompareProduct
                                        placement={inGridPlacement}
                                        products={[product]}
                                        inventoryAware={
                                            inventory_aware_listings
                                        }
                                        product_tile_data_source={
                                            product_tile_data_source
                                        }
                                        // this needs to be revisited, as we cannot drop the item entirely
                                        // when inventory is zero becuase it leaves a empty space in the grid
                                        // we need to find a way to update the grid injection when product is out of stock
                                        displayOnZeroInventory={true}
                                        itemNumber={product.ParentSKU}
                                        // @ts-expect-error - Partial config data
                                        configData={productTileConfigData}
                                        isSponsored={true}
                                        sponsoredDisplayVariant={
                                            productTileConfigData
                                                ?.translationsConfig
                                                ?.sponsoredProductDisplayStyle
                                                ?.variant
                                        }
                                        sx={{
                                            height: '100%',
                                        }}
                                        WrapperComponent={(
                                            props: React.PropsWithChildren,
                                        ) => {
                                            if (doc.criteoIndex !== 0) {
                                                return (
                                                    <Grid
                                                        key={`${product.ParentSKU}_criteo_${doc.criteoIndex}_wrapper`}
                                                        size={{
                                                            xs: singleItemGridSize,
                                                        }}
                                                    >
                                                        {props.children}
                                                    </Grid>
                                                );
                                            } else {
                                                return (
                                                    <Grid
                                                        key={`${product.ParentSKU}_criteo_${doc.criteoIndex}_wrapper`}
                                                        size={{
                                                            xs: singleItemGridSize,
                                                        }}
                                                    >
                                                        <AnalyticsObserver
                                                            observer={{
                                                                minimumViewTime:
                                                                    analytics.minimumViewTime,
                                                                onLoad: () => {
                                                                    if (
                                                                        !criteoInGridOnLoadBeaconFired
                                                                    ) {
                                                                        analytics.onPlacementLoad();
                                                                        criteoInGridOnLoadBeaconFired = true;
                                                                    }
                                                                },
                                                                onView: () =>
                                                                    analytics.onPlacementViewed(),
                                                                viewThreshold:
                                                                    analytics.viewThreshold,
                                                            }}
                                                            sx={{
                                                                height: '100%',
                                                            }}
                                                        >
                                                            {props.children}
                                                        </AnalyticsObserver>
                                                    </Grid>
                                                );
                                            }
                                        }}
                                    />
                                </Analytics>
                            );
                        }
                    } else {
                        // Skeleton: this will render on the server until hydrated on the client to get into the above if statement
                        ProductTileComponent = (
                            <Grid
                                size={{
                                    xs: 12,
                                }}
                                key={`criteo_${doc.criteoIndex}_skeleton`}
                            >
                                <ProductTileSkeleton />
                            </Grid>
                        );
                    }
                } else if (
                    doc.isCriteoButterflyAd ||
                    doc.isCriteoButterflyFiller
                ) {
                    // Criteo Butterfly
                    if (doc.isCriteoButterflyFiller) {
                        return <></>; // This is to keep the index correct for future tiles, but shouldn't be rendered
                    }
                    const butterflyPlacement =
                        doc.criteoIndex !== undefined
                            ? butterflyPlacements[doc.criteoIndex]
                            : undefined;
                    const products = butterflyPlacement?.products;
                    const ParentSKUs =
                        products?.map(
                            ({ ParentSKU }: { ParentSKU: string }) => ParentSKU,
                        ) || [];

                    return (
                        <Grid
                            size={{
                                xs: singleItemGridSize * 2,
                            }}
                            key={`butterfly_${doc.criteoIndex}_${index}`}
                            sx={{
                                display: 'flex',
                            }}
                        >
                            <Box
                                sx={{
                                    minWidth: '100%',
                                    minHeight: '100%',
                                }}
                            >
                                <CriteoButterflyDisplay
                                    a11yCriteo={a11yCriteo}
                                    contentStackEntry={{
                                        // @ts-expect-error - criteo_placement_name is dynamically added
                                        criteo_placement_name: `butterfly_${doc.criteoIndex}`,
                                        product_display_style: {
                                            ...product_display_style,
                                            display_on_zero_inventory: true, // this needs revisited, same as ingrid product tiles
                                        },
                                    }}
                                    locale={lang}
                                    products={butterflyPlacement?.products!}
                                    placement={butterflyPlacement!}
                                    ParentSKUs={ParentSKUs}
                                    productTileSx={{
                                        minHeight: isMobile ? '100%' : '97%',
                                        paddingRight: `2px`,
                                    }}
                                />
                            </Box>
                        </Grid>
                    );
                } else {
                    const lwData =
                        searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS
                            ? (doc as ExtendedSearchResultLW)
                            : undefined;

                    const grsData =
                        searchService === SEARCH_SERVICE_PROVIDERS.GRS
                            ? (doc as ExtendedSearchResultGRS)
                            : undefined;

                    const isTravelItem =
                        !!lwData?.content_type?.includes(TRAVEL_DESTINATION) ||
                        !!grsData?.product?.attributes[
                            'product_type'
                        ]?.text?.[0]?.includes(TRAVEL_DESTINATION);

                    ProductTileComponent = (
                        <Grid
                            size={{
                                xs: singleItemGridSize,
                            }}
                            key={
                                lwData
                                    ? `${lwData?.item_number}_${index}`
                                    : `${grsData?.id}_${index}`
                            }
                            sx={{
                                display: 'flex',
                            }}
                        >
                            <Analytics
                                analyticData={{
                                    component: isTravelItem
                                        ? {
                                              identifier: SEARCH,
                                              type: COMPONENT_TYPE_UI,
                                          }
                                        : undefined,
                                    trackedData: !isTravelItem
                                        ? [
                                              {
                                                  key:
                                                      pageType === 'search'
                                                          ? KEY_SEARCH_INDEX
                                                          : DISPLAY_RANK,
                                                  value: index.toString(),
                                              },
                                          ]
                                        : undefined,
                                    trackType: isTravelItem
                                        ? TRACK_TYPE_NAVIGATION
                                        : TRACK_TYPE_MULTI_ATTR,
                                }}
                            >
                                <ProductTileUI
                                    tileIndex={index}
                                    variant={product_card_display_style}
                                    locale={lang}
                                    showActionButton={show_action_button}
                                    showMultiVariantsAvailability={
                                        showMultiVariantsAvailability
                                    }
                                    showReviews={show_reviews}
                                    lwData={lwData}
                                    // @ts-expect-error - TODO
                                    grsData={grsData}
                                    inventoryAware={inventory_aware_listings}
                                    showCompareProduct
                                    // @ts-expect-error - TODO
                                    configData={productTileConfigData}
                                    pageType={pageType}
                                    doFocus={
                                        doFocusFirstItem && index === 0
                                            ? doFocusFirstItem
                                            : false
                                    }
                                />
                            </Analytics>
                        </Grid>
                    );
                }

                let FullRowAdComponent;
                const row = (index + 1) / columnCount;

                if (
                    hasInGridFullRowAds &&
                    typeof rowToRenderFullRowAdOn === 'number' &&
                    row % rowToRenderFullRowAdOn === 0
                ) {
                    const adIndex = row / rowToRenderFullRowAdOn - 1;

                    const fullRowAdData =
                        composerData?.inGridPlacements?.[adIndex];

                    if (fullRowAdData) {
                        FullRowAdComponent = fullRowAdEntriesArray[adIndex];
                    }
                }

                const lwData =
                    searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS
                        ? (doc as ExtendedSearchResultLW)
                        : undefined;

                const grsData =
                    searchService === SEARCH_SERVICE_PROVIDERS.GRS
                        ? (doc as ExtendedSearchResultGRS)
                        : undefined;

                return (
                    <React.Fragment key={index}>
                        {ProductTileComponent}
                        {FullRowAdComponent && (
                            <Grid
                                size={{
                                    xs: 12,
                                }}
                                key={`${lwData?.item_number ?? grsData?.id}_${index}_fullRow`}
                            >
                                {FullRowAdComponent}
                            </Grid>
                        )}
                    </React.Fragment>
                );
            })}
        </Grid>
    );
}
